import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, ArrowLeft, Check, Headphones, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import CartDrawer from "../components/site/CartDrawer";

const aud = (n) => `$${Number(n).toFixed(0)}`;

function MiniHeader() {
  const { count, setOpen } = useCart();
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#F8F9FA]/80 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 font-display font-black text-lg tracking-tight uppercase" data-testid="logo"><img src="/logo.png?v=2" alt="Indian TV Box logo" className="w-9 h-9 rounded-lg" /><span>INDIANTV<span className="text-[#FF5722]">.</span>BOX</span></Link>
        <button onClick={() => setOpen(true)} data-testid="open-cart-btn" className="relative p-2">
          <ShoppingCart size={20} />
          {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#FF5722] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
        </button>
      </div>
    </header>
  );
}

export default function PlanDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add, setOpen } = useCart();
  const [plan, setPlan] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/plans/${id}`).then((r) => setPlan(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#F8F9FA]">
      <p className="font-display font-bold text-2xl">Subscription not found</p>
      <Link to="/" className="text-[#FF5722] font-bold">← Back to store</Link>
    </div>
  );
  if (!plan) return <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] font-mono-x text-sm">Loading…</div>;

  const detail = plan.detail || {};
  const addToCart = () => { add({ ref_id: plan.id, name: plan.name, kind: "plan", price: plan.price, image: plan.image }); toast.success(`${plan.name} added`); };
  const buyNow = () => { addToCart(); setOpen(true); };

  return (
    <div className="bg-[#F8F9FA] min-h-screen">
      <MiniHeader />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-24">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[#0F172A] mb-8" data-testid="back-btn"><ArrowLeft size={16} /> Back</button>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden flex items-center justify-center p-8">
            <img src={plan.image} alt={plan.name} className="w-full h-[360px] object-contain" data-testid="plan-image" />
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[#FF5722]">Subscription</p>
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl text-[#0F172A] mt-2 leading-[0.95]" data-testid="plan-title">{plan.name}</h1>
            <div className="mt-4 flex items-end gap-1">
              <span className="font-display font-black text-4xl text-[#0F172A]" data-testid="plan-price">{aud(plan.price)}</span>
              <span className="text-slate-500 mb-1 font-medium">/{plan.period}</span>
            </div>
            <p className="text-base font-medium text-slate-600 mt-4 leading-relaxed">{plan.description}</p>

            {plan.features?.length > 0 && (
              <ul className="mt-6 space-y-2">
                {plan.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm font-medium text-slate-700"><Check size={16} className="text-[#00C473] mt-0.5 shrink-0" /> {f}</li>)}
              </ul>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={buyNow} data-testid="plan-buy-now" className="bg-[#FF5722] text-white rounded-full px-8 py-4 font-display font-bold uppercase tracking-wide hover:bg-[#0F172A] transition-colors">Buy now</button>
              <button onClick={addToCart} data-testid="plan-add-cart" className="rounded-full px-8 py-4 font-display font-bold uppercase tracking-wide border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-colors">Add to cart</button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-bold text-slate-500">
              <span className="flex items-center gap-2"><Headphones size={15} className="text-[#4F46E5]" /> Aussie support 7 days</span>
              <span className="flex items-center gap-2"><ShieldCheck size={15} className="text-[#4F46E5]" /> Prepaid · secure</span>
            </div>
          </div>
        </div>

        {detail.how?.length > 0 && (
          <div className="mt-24">
            <h2 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl text-[#0F172A]">How it works</h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {detail.how.map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  className="bg-white border border-slate-200 rounded-3xl p-6">
                  <span className="font-display font-black text-4xl text-[#FF5722]">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-3 text-sm font-medium text-slate-600 leading-relaxed">{s}</p>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {detail.faqs?.length > 0 && (
          <div className="mt-24 max-w-3xl">
            <h2 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl text-[#0F172A]">FAQs</h2>
            <Accordion type="single" collapsible className="mt-8" data-testid="plan-faq">
              {detail.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="border-b border-slate-200 py-2">
                  <AccordionTrigger className="font-display font-bold text-base sm:text-lg text-left text-[#0F172A] hover:text-[#4F46E5] hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base font-medium text-slate-600 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
      </div>
      <CartDrawer />
    </div>
  );
}
